import ChangedField from "./ChangedField";

import LocationIcon from "../../../assets/location-pin.svg?react";
import * as symbolUtils from "@arcgis/core/symbols/support/symbolUtils";
import "./EditedFeaturePanel.css";
import Collapsible from "../../genericComponents/Collapsible/Collapsible";
import { GetCodedValueAndAlias, getLayerName, isMainFeatureByLayerId, zoomTo } from "../../../utils/arcgisUtils";
import { useContext, useEffect, useState } from "react";
import {
    addGraphicsToJobsGraphicsLayer,
    objectShouldValidate,
    objectsShouldValidate,
    removeGraphicsFromJobsGraphicsLayer,
} from "../../../utils/jobsUtils";
import { AppContext, JobContext } from "../../../Context";
import { LeftPanelTabs } from "../../../constants/Panels";
import { setLeftPanelActiveTab } from "../../../store/appSlice";
import { useDispatch } from "react-redux";
import { FeatureObject, FeatureWithChanges } from "../../../utils/newVersioningUtils";
import ValidationResult from "./ValidationResult";
import { JobsStatus } from "../../../constants/Jobs";
import type Graphic from "@arcgis/core/Graphic";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface EditedFeaturePanelForOperativeCompareProps {
    editedFeature: FeatureWithChanges;
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures: () => void;
    jobStatus: JobsStatus;
}

const EditedFeaturePanelForOperativeCompare = (props: EditedFeaturePanelForOperativeCompareProps) => {
    const dispatch = useDispatch();

    const [symbolHtmlElement, setSymbolHtmlElement] = useState<HTMLElement | null>(null);
    const [isOriginalFeatureVisible, setIsOriginalFeatureVisible] = useState(false);
    const appContext = useContext(AppContext);
    const jobContext = useContext(JobContext);
    const validateObjects =
        objectsShouldValidate(jobContext?.selectedJob.value?.attributes.jobb_type) &&
        objectShouldValidate(props.editedFeature);

    /* Note 31.10.2025 - Leaving, but commented out for a month in case KYV changes their mind again. */
    // const [objectWorkOrderIds, setObjectWorkOrderIds] = useState<string[]>([]);

    const getTitle = () => {
        let sjomerke_nr = "";
        for (const key in props.editedFeature.mainFeature.versionGraphic?.attributes) {
            if (key === "sjomerke_nr") {
                sjomerke_nr = props.editedFeature.mainFeature.versionGraphic?.attributes[key];
                break;
            }
        }
        if (sjomerke_nr === "" || sjomerke_nr === null) {
            sjomerke_nr =
                props.editedFeature.mainFeature.versionGraphic?.attributes["navn"] ??
                "ObjId:" +
                    props.editedFeature.mainFeature.objectId +
                    " - " +
                    getLayerName(props.editedFeature.mainFeature.layerId);
        }

        return sjomerke_nr;
    };

    const getChanges = () => {
        const changes: React.JSX.Element[] = [];
        if (props.editedFeature.mainFeature.type === "update" || props.editedFeature.mainFeature.type === "insert") {
            for (const field of props.editedFeature.mainFeature.changedFieldsOperative ?? []) {
                changes.push(
                    <ChangedField editedFeature={props.editedFeature.mainFeature} field={field} fromOperative={true} />
                );
            }
        }

        return changes;
    };

    const getRelatedFeatures = () => {
        const relatedChanges: React.JSX.Element[] = [];
        const changesByLayer: { [key: number]: FeatureObject[] } = {};
        for (const relatedFeature of props.editedFeature.relatedFeatures) {
            if (relatedFeature.type === "update" || relatedFeature.type === "insert") {
                if (!changesByLayer[relatedFeature.layerId]) {
                    changesByLayer[relatedFeature.layerId] = [];
                }
                changesByLayer[relatedFeature.layerId].push(relatedFeature);
            }
        }

        const mainFeatureElements: React.JSX.Element[] = [];
        const relatedFeatureElements: React.JSX.Element[] = [];

        for (const layerId in changesByLayer) {
            if (isMainFeatureByLayerId(Number(layerId))) {
                const layerName = getLayerName(Number.parseInt(layerId));

                for (const mainFeature of changesByLayer[layerId]) {
                    const collapsible = (
                        <Collapsible
                            isOpen={false}
                            setIsOpen={() => {}}
                            title={
                                layerName +
                                " - " +
                                (mainFeature.versionGraphic?.attributes.sjomerke_nr ??
                                    "ObjId:" + mainFeature.versionGraphic?.attributes.objectid)
                            }
                            messageWhenEmpty="Ingen endringer funnet"
                        >
                            {mainFeature.changedFieldsOperative?.map((field) => (
                                <ChangedField
                                    key={`${mainFeature.layerId}-${mainFeature.objectId}-related-${field}`}
                                    editedFeature={mainFeature}
                                    field={field}
                                    fromOperative={true}
                                />
                            ))}
                        </Collapsible>
                    );
                    mainFeatureElements.push(collapsible);
                }
            } else {
                relatedChanges.push(
                    <div key={layerId} className="flex-row w-100 space-between">
                        <span className="job-card-key">{getLayerName(Number.parseInt(layerId))}</span>
                        {/* <span className="job-card-value bold">
                            Antall: {changesByLayer[layerId].length}
                        </span> */}
                    </div>
                );
            }
        }
        if (relatedFeatureElements.length > 0) {
            relatedChanges.push(...relatedFeatureElements);
        }
        if (mainFeatureElements.length > 0) {
            relatedChanges.push(...mainFeatureElements);
        }

        if (relatedChanges.length === 0) {
            return null;
        } else {
            return (
                <Collapsible
                    isOpen={false}
                    setIsOpen={() => {}}
                    title="Relaterte objekter"
                    className="edited-feature-panel-collapsible"
                    messageWhenEmpty="Ingen endringer funnet"
                >
                    {relatedChanges}
                </Collapsible>
            );
        }
    };

    const zoomToFeature = async () => {
        if (!props.editedFeature) return;

        const mainFeature = props.editedFeature.mainFeature.versionGraphic;
        if (!mainFeature) return;

        props.clearHighlightedFeatures();
        zoomTo(mainFeature, props.storeHighlightHandle, "temporary");

        appContext?.shownFeature.set(mainFeature);

        dispatch(setLeftPanelActiveTab(LeftPanelTabs.VIEWER));
    };

    const showHideFeature = () => {
        if (isOriginalFeatureVisible) {
            setIsOriginalFeatureVisible(false);
            const graphicsToRemove: Graphic[] = [];

            if (props.editedFeature.mainFeature.operativeGraphic) {
                graphicsToRemove.push(props.editedFeature.mainFeature.operativeGraphic);
            }
            for (const relatedFeature of props.editedFeature.relatedFeatures) {
                if (relatedFeature.operativeGraphic) {
                    graphicsToRemove.push(relatedFeature.operativeGraphic);
                }
            }
            removeGraphicsFromJobsGraphicsLayer(graphicsToRemove);
        } else {
            const graphicsToAdd: Graphic[] = [];
            if (props.editedFeature.mainFeature.operativeGraphic) {
                graphicsToAdd.push(props.editedFeature.mainFeature.operativeGraphic);
            } else {
                console.warn("No operative graphic found for edited feature.");
            }
            for (const relatedFeature of props.editedFeature.relatedFeatures) {
                if (relatedFeature.operativeGraphic) {
                    graphicsToAdd.push(relatedFeature.operativeGraphic);
                }
            }
            addGraphicsToJobsGraphicsLayer(graphicsToAdd);
            setIsOriginalFeatureVisible(true);
        }
    };

    // const restoreObject = () => {
    //     const nauticalObject = { mainFeature: props.editedFeature, relatedFeatures: props.relatedFeatures };
    //     props.restoreFeature(props.layerId, nauticalObject);
    // };

    useEffect(() => {
        return () => {
            if (isOriginalFeatureVisible) {
                showHideFeature();
            }
        };
    }, [isOriginalFeatureVisible]);

    useEffect(() => {
        if (props.editedFeature.mainFeature.versionGraphic) {
            symbolUtils.getDisplayedSymbol(props.editedFeature.mainFeature.versionGraphic).then((symbol) => {
                if (symbol) {
                    symbolUtils.renderPreviewHTML(symbol, { size: 12 }).then((symbolElement) => {
                        if (!symbolElement) return;
                        setSymbolHtmlElement(symbolElement);
                    });
                } else {
                    setSymbolHtmlElement(null);
                }
            });
        } else {
            setSymbolHtmlElement(null);
        }

        {
            /* Note 31.10.2025 - Leaving, but commented out for a month in case KYV changes their mind again. */
        }
        // const jobObjectId = jobsContext?.selectedJob.value?.attributes.objectid;
        // if (!jobObjectId) {
        //     setObjectWorkOrderIds([]);
        //     return;
        // }
        // getJobWorkOrdersFromJob(jobObjectId).then((workOrders) => {
        //     if (workOrders.length > 0) {
        //         const featureLayerId = props.editedFeature.mainFeature.layerId;
        //         const featureGlobalId = props.editedFeature.mainFeature.versionGraphic?.attributes.globalid;
        //         const matchingWorkOrders = workOrders.filter(wo => wo.layerId === featureLayerId && wo.globalId === featureGlobalId);
        //         setObjectWorkOrderIds(matchingWorkOrders.map(wo => wo.workOrderId));
        //     } else {
        //         setObjectWorkOrderIds([]);
        //     }
        // });
    }, [props.editedFeature]);

    return (
        <div className="edited-feature-panel">
            <div className="edited-feature-panel-header">
                <div
                    className="search-item-symbol"
                    dangerouslySetInnerHTML={{ __html: symbolHtmlElement?.outerHTML ?? "" }}
                ></div>
                {props.jobStatus !== JobsStatus.AnalyseFeilet &&
                    props.jobStatus !== JobsStatus.KontrollskjermingAvvik &&
                    props.jobStatus !== JobsStatus.AnalyseGjennomfort &&
                    props.jobStatus !== JobsStatus.AnalyseStartet &&
                    props.jobStatus !== JobsStatus.DataLastetOpp && (
                        <button
                            className="nfs-button no-flex fit-content outline"
                            style={{ padding: "0rem 0.5rem" }}
                            onClick={showHideFeature}
                        >
                            {isOriginalFeatureVisible ? "Skjul" : "Vis"}
                        </button>
                    )}
                <span>{getTitle()}</span>
                <button className="empty-button" onClick={zoomToFeature}>
                    <LocationIcon />
                </button>
            </div>
            <div className="flex-row w-100 space-between">
                <span className="job-card-key">Status</span>
                <span className="job-card-value bold">
                    {
                        GetCodedValueAndAlias(
                            props.editedFeature.mainFeature.versionGraphic?.layer,
                            "nfs_status",
                            props.editedFeature.mainFeature.versionGraphic?.attributes.nfs_status
                        )[1]
                    }
                </span>
            </div>
            <div className="flex-row w-100 space-between">
                <span className="job-card-key">Navn</span>
                <span className="job-card-value bold">
                    {props.editedFeature.mainFeature.versionGraphic?.attributes.navn}
                </span>
            </div>
            <div className="flex-row w-100 space-between">
                <span className="job-card-key">Sted</span>
                <span className="job-card-value bold">
                    {props.editedFeature.mainFeature.versionGraphic?.attributes.sted}
                </span>
            </div>

            {validateObjects && props.jobStatus === JobsStatus.QA2 && (
                <ValidationResult validationResult={props.editedFeature.validationResult} />
            )}

            {/* Note 31.10.2025 - Leaving, but commented out for a month in case KYV changes their mind again. */}
            {/* <div className="flex-row w-100 space-between">
                <span className="job-card-key">Arbeidsordre</span>
                <span className="job-card-value bold">
                    {objectWorkOrderIds.map((id) => (
                        <a
                            key={id}
                            href={`${AppConfig.FDV.BaseUrl}${AppConfig.FDV.WorkOrderBase}${id}`}
                            target="_blank"
                            rel="noopener"
                            className="fit-content empty-button center"
                        >
                            {id}
                            <OpenInNewWindowIcon />
                        </a>
                    ))}
                </span>
            </div> */}
            {props.jobStatus !== JobsStatus.AnalyseFeilet &&
                props.jobStatus !== JobsStatus.KontrollskjermingAvvik &&
                props.jobStatus !== JobsStatus.AnalyseGjennomfort &&
                props.jobStatus !== JobsStatus.AnalyseStartet &&
                props.jobStatus !== JobsStatus.DataLastetOpp && (
                    <>
                        <br />
                        <span>Viser endringer sammenlignet med sist operativ</span>
                        <Collapsible
                            isOpen={false}
                            setIsOpen={() => {}}
                            title="Vis endringer"
                            className="edited-feature-panel-collapsible"
                            messageWhenEmpty="Ingen endringer funnet"
                        >
                            {getChanges()}
                        </Collapsible>
                        {getRelatedFeatures()}
                    </>
                )}
        </div>
    );
};

export default EditedFeaturePanelForOperativeCompare;
